import React, { useState } from 'react';
import {
  createBrowserRouter,
  RouterProvider,
  createRoutesFromElements,
  Route,
  Outlet,
  redirect,
  useNavigate
} from 'react-router-dom';
import { AuthProvider, useAuth, TokenPurchaseModalContext } from './context/AuthContext';
import DirectTokenPurchaseModal from './components/auth/DirectTokenPurchaseModal';
import { ThemeProvider } from './context/ThemeContext';
import { TokenModalProvider } from './context/TokenModalContext';
import Header from './components/Header';
import WelcomeDialog from './components/auth/WelcomeDialog';
import Home from './pages/Home';
import Viajeros from './pages/Viajeros';
import Mercancias from './pages/Mercancias';
import Modulos from './pages/Modulos';
import TestPractica from './pages/TestPractica';
import TestAleatorio from './pages/TestAleatorio';
import Repaso from './pages/Repaso';
import NotFound from './pages/NotFound';
import SchoolAdmin from './pages/SchoolAdmin';
import AccesoAutoescuela from './pages/AccesoAutoescuela';
import IniciarSesion from './pages/IniciarSesion';
import RegistrarAutoescuela from './pages/RegistrarAutoescuela';
import SessionTest from './pages/SessionTest';
import Privacidad from './pages/Privacidad';
import AvisoLegal from './pages/AvisoLegal';
import ProtectedRoute from './components/auth/ProtectedRoute';
import { getApiUrl, API_ENDPOINTS } from './config/api';
import authService from './services/auth';
import './App.css';
import './styles/loading.css';

function TokenValidationWrapper() {
  const [showWelcome, setShowWelcome] = useState(false);
  const [welcomeData, setWelcomeData] = useState(null);
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleTokenValidation = async (formData) => {
    const token = formData.get('token').trim();

    // Basic validation
    if (!token) {
      return { error: 'Por favor, introduce un token de acceso' };
    }

    // Format validation using new TokenManager through authService
    const formatValidation = authService.tokenManager.validateFormat(token);
    if (!formatValidation.isValid) {
      return { error: formatValidation.error || 'Formato de token inválido' };
    }

    try {
      // Validate token
      const response = await fetch(getApiUrl(API_ENDPOINTS.validateToken), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ token })
      });

      const data = await response.json();

      if (!response.ok || !data.success) {
        return { error: data.message || 'Token inválido' };
      }

      // Calculate remaining days
      const expiresAt = new Date(data.expiresAt);
      const now = new Date();
      const remainingDays = Math.ceil((expiresAt - now) / (1000 * 60 * 60 * 24));

      // Store complete token data for the dialog and login
      setWelcomeData({
        studentName: data.student.name,
        remainingDays,
        token: data.token,
        student: data.student,
        expiresAt: data.expiresAt
      });
      setShowWelcome(true);

      return null;
    } catch (error) {
      console.error('Token validation error:', error);
      return {
        error: 'Error al validar el token. Por favor, comprueba tu conexión e inténtalo de nuevo.'
      };
    }
  };

  const handleWelcomeClose = async () => {
    setShowWelcome(false);
    if (welcomeData) {
      try {
        // Login with the complete token data
        await login({
          token: welcomeData.token,
          student: welcomeData.student,
          expiresAt: welcomeData.expiresAt
        });
      } catch (error) {
        console.error('Login error:', error);
      }
    }
  };

  return (
    <>
      <WelcomeDialog
        isOpen={showWelcome}
        onClose={handleWelcomeClose}
        studentName={welcomeData?.studentName}
        remainingDays={welcomeData?.remainingDays}
      />
      <Outlet context={{ handleTokenValidation }} />
    </>
  );
}

async function tokenAction({ request }) {
  const formData = await request.formData();
  return formData;
}

function Layout({ children }) {
  React.useEffect(() => {
    const handleKeyDown = (e) => {
      // Prevenir navegación hacia atrás con la tecla borrar
      if (e.key === 'Backspace' &&
          // Solo prevenir si no estamos en un campo de entrada
          !['INPUT', 'TEXTAREA'].includes(document.activeElement.tagName) &&
          // Y si no hay texto seleccionado
          !window.getSelection().toString()) {
        e.preventDefault();
      }
    };

    // Añadir el event listener
    window.addEventListener('keydown', handleKeyDown);

    // Limpiar el event listener al desmontar
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, []);

  return (
    <div className="content-wrapper">
      <div className="header-background" />
      <Header />
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
        {children}
      </div>
      <div className="loading-overlay">
        <div className="loading-spinner"></div>
      </div>
    </div>
  );
}

function AppRoot() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <TokenModalProvider>
          <Layout>
            <DirectTokenPurchaseModal />
            <Outlet />
          </Layout>
        </TokenModalProvider>
      </AuthProvider>
    </ThemeProvider>
  );
}

// Create router with all future flags enabled
const router = createBrowserRouter(
  createRoutesFromElements(
    <Route element={<AppRoot />}>
      <Route element={<TokenValidationWrapper />}>
        {/* Public routes */}
        <Route path="/" element={<Home />} />
        <Route path="/viajeros" element={<Viajeros />} action={tokenAction} />
        <Route path="/mercancias" element={<Mercancias />} action={tokenAction} />
        <Route
          path="/modulos"
          element={
            <ProtectedRoute allowFreeTrial>
              <Modulos />
            </ProtectedRoute>
          }
        />
        <Route path="/acceso-autoescuela" element={<AccesoAutoescuela />} />
        <Route path="/iniciar-sesion" element={<IniciarSesion />} />
        <Route path="/registrar-nueva-autoescuela" element={<RegistrarAutoescuela />} />
        <Route path="/privacidad" element={<Privacidad />} />
        <Route path="/aviso-legal" element={<AvisoLegal />} />
        
        {/* School dashboard route */}
        <Route
          path="/dashboard"
          element={
            <ProtectedRoute>
              <SchoolAdmin />
            </ProtectedRoute>
          }
        />

        {/* Test routes - allow free trial */}
        <Route
          path="/test-practica"
          element={
            <ProtectedRoute allowFreeTrial>
              <TestPractica />
            </ProtectedRoute>
          }
          action={tokenAction}
        />
        <Route
          path="/test-aleatorio"
          element={
            <ProtectedRoute allowFreeTrial>
              <TestAleatorio />
            </ProtectedRoute>
          }
          action={tokenAction}
        />

        {/* Protected routes - require authentication */}
        <Route
          path="/repaso"
          element={
            <ProtectedRoute allowFreeTrial>
              <Repaso />
            </ProtectedRoute>
          }
        />
{/* Admin routes */}
<Route
  path="/admin"
  element={
    <ProtectedRoute requiredRole="admin" redirectTo="/unauthorized">
      <SchoolAdmin />
    </ProtectedRoute>
  }
/>

{/* Debug routes */}
<Route
  path="/session-test"
  element={<SessionTest />}
/>

{/* 404 route */}
        {/* 404 route */}
        <Route path="*" element={<NotFound />} />
      </Route>
    </Route>
  ),
  {
    future: {
      v7_startTransition: true,
      v7_relativeSplatPath: true,
      v7_fetcherPersist: true,
      v7_normalizeFormMethod: true,
      v7_partialHydration: true,
      v7_skipActionErrorRevalidation: true
    }
  }
);

function App() {
  return (
    <RouterProvider 
      router={router}
      future={{
        v7_startTransition: true,
        v7_relativeSplatPath: true,
        v7_fetcherPersist: true,
        v7_normalizeFormMethod: true,
        v7_partialHydration: true,
        v7_skipActionErrorRevalidation: true
      }}
    />
  );
}

export default App;
